import { cleanValue, formatValue } from 'react-currency-input-field';
import type {
  CurrencyInputOnChangeValues,
  IntlConfig,
} from 'react-currency-input-field';
import { PINNED_NUMBER_FORMAT } from '@/constants/numberFormats';

export interface NumericInputOptions {
  allowDecimals?: boolean;
  allowNegativeValue?: boolean;
  decimalsLimit?: number;
  intlConfig?: IntlConfig;
  prefix?: string;
}

interface ResolvedOptions extends Required<NumericInputOptions> {
  groupSeparator: string;
  decimalSeparator: string;
}

const ABBREVIATIONS = ['k', 'm', 'b'];

const resolveOptions = ({
  allowDecimals = true,
  allowNegativeValue = true,
  decimalsLimit = 2,
  intlConfig = PINNED_NUMBER_FORMAT,
  prefix = '',
}: NumericInputOptions): ResolvedOptions => {
  // Seven digits and a fraction: locales that suppress grouping on shorter
  // integers still emit a `group` part for this one.
  const parts = new Intl.NumberFormat(intlConfig.locale).formatToParts(
    1234567.5,
  );

  return {
    allowDecimals,
    allowNegativeValue,
    decimalsLimit,
    intlConfig,
    prefix,
    groupSeparator: parts.find(({ type }) => type === 'group')?.value ?? ',',
    decimalSeparator:
      parts.find(({ type }) => type === 'decimal')?.value ?? '.',
  };
};

/**
 * Keeps what the user typed, minus the characters this field cannot hold.
 * Group separators survive wherever they were typed, which is what lets
 * `1,000` be typed a character at a time; they are dropped from the value,
 * not from the display.
 */
export const sanitizeNumericDraft = (
  raw: string,
  options: NumericInputOptions = {},
): string => {
  const {
    allowDecimals,
    allowNegativeValue,
    decimalsLimit,
    groupSeparator,
    decimalSeparator,
    prefix,
  } = resolveOptions(options);

  // A sign counts wherever it lands ahead of the number: with a prefix on
  // screen the caret sits after it, so '$-50' is how a negative gets typed.
  const firstDigit = raw.search(/\d/);
  const beforeDigits = firstDigit === -1 ? raw : raw.slice(0, firstDigit);
  const negative = allowNegativeValue && beforeDigits.includes('-');

  const unsigned = raw.replace(/-/g, '');
  // The prefix belongs to the field, not to what was typed: it is stripped off
  // the incoming text and put back below, so it stays on screen through every
  // keystroke and cannot be deleted.
  const digits =
    prefix !== '' && unsigned.startsWith(prefix)
      ? unsigned.slice(prefix.length)
      : unsigned;

  let body = '';
  let decimals: number | null = null;

  for (let index = 0; index < digits.length; index += 1) {
    const char = digits[index];

    if (/\d/.test(char)) {
      if (decimals === null) {
        body += char;
      } else if (decimals < decimalsLimit) {
        decimals += 1;
        body += char;
      }
      continue;
    }

    if (char === groupSeparator && decimals === null) {
      body += char;
      continue;
    }

    if (char === decimalSeparator && allowDecimals && decimals === null) {
      decimals = 0;
      body += char;
      continue;
    }

    if (
      index === digits.length - 1 &&
      ABBREVIATIONS.includes(char.toLowerCase()) &&
      /\d/.test(body)
    ) {
      body += char.toLowerCase();
    }
  }

  return `${negative ? '-' : ''}${body === '' ? '' : prefix}${body}`;
};

export const formatNumericValue = (
  value: string,
  options: NumericInputOptions = {},
): string => {
  const { intlConfig, groupSeparator, decimalSeparator, prefix } =
    resolveOptions(options);

  return formatValue({
    value,
    intlConfig,
    groupSeparator,
    decimalSeparator,
    prefix,
  });
};

/**
 * The value and the `values` payload a draft resolves to, matching what
 * react-currency-input-field reports for the same string.
 */
export const numericInputValues = (
  draft: string,
  options: NumericInputOptions = {},
): {
  value: string | undefined;
  values: CurrencyInputOnChangeValues;
} => {
  const {
    allowDecimals,
    allowNegativeValue,
    decimalsLimit,
    groupSeparator,
    decimalSeparator,
    prefix,
  } = resolveOptions(options);

  const value = cleanValue({
    value: draft,
    allowDecimals,
    allowNegativeValue,
    decimalsLimit,
    groupSeparator,
    decimalSeparator,
    prefix,
  });

  // Anything without a digit is still being typed — '', '-', '.', '-.' — and
  // reporting it would put a NaN into the form.
  if (!/\d/.test(value)) {
    return {
      value: undefined,
      values: { float: null, formatted: '', value: '' },
    };
  }

  return {
    value,
    values: {
      float: parseFloat(value.replace(decimalSeparator, '.')),
      formatted: formatNumericValue(value, options),
      value,
    },
  };
};
